import { useCallback, useState } from 'react'
import { useCreateESignForVersion } from '../api/eSignHooks'

export interface UseSigningFlowOptions {
	documentIdFromUrl: string | undefined
}

interface OpenCreateESignDraftParams {
	fileId: string
	versionId?: string | null
	sourceESignObjectId?: string | null
}

export function useSigningFlow(_options: UseSigningFlowOptions) {
	const {
		mutateAsync: createESignForVersion,
		isPending: isCreateESignObjectPending,
	} = useCreateESignForVersion()

	const [isCreateDraftModalOpen, setIsCreateDraftModalOpen] = useState(false)
	const [createDraftParams, setCreateDraftParams] =
		useState<OpenCreateESignDraftParams | null>(null)

	const onSendToESign = useCallback(
		({
			fileId,
			versionId,
			sourceESignObjectId = null,
		}: OpenCreateESignDraftParams) => {
			if (!fileId) return
			setCreateDraftParams({ fileId, versionId, sourceESignObjectId })
			setIsCreateDraftModalOpen(true)
		},
		[],
	)

	const closeCreateDraftModal = useCallback(() => {
		setIsCreateDraftModalOpen(false)
		setCreateDraftParams(null)
	}, [])

	const createESignDraft = useCallback(
		async (name: string, versionIdOverride?: string | null) => {
			if (!createDraftParams?.fileId) return null

			const versionId = versionIdOverride ?? createDraftParams.versionId
			if (!versionId) {
				console.error('versionId is required for creating eSign')
				return null
			}

			const created = await createESignForVersion({
				fileId: createDraftParams.fileId,
				versionId,
				title: name,
				sourceESignId: createDraftParams.sourceESignObjectId ?? null,
			})

			const createdId = created?.eSign?.id
			if (!createdId) return null

			setCreateDraftParams(null)
			setIsCreateDraftModalOpen(false)
			return {
				documentId: createDraftParams.fileId,
				eSignObjectId: String(createdId),
				versionId: versionId,
			}
		},
		[createDraftParams, createESignForVersion],
	)

	return {
		isCreateDraftModalOpen,
		createDraftParams,
		onSendToESign,
		closeCreateDraftModal,
		createESignDraft,
		isCreateESignObjectPending,
	}
}
